import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ShieldCheck, Search, RefreshCw, ExternalLink, Save, X, Gift, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDateLong } from '@/lib/format';

/**
 * Vyndesk Süper-Admin — tüm müşteri lisanslarını yönetir. Yalnız Vyndesk desk'inde
 * (backend /api/superadmin/status enabled) görünür; anahtar frontend'e inmez.
 * Metinler: i18n namespace "admin".
 */

interface Ent { plan_name?: string; included: Record<string, number | null>; bonus?: Record<string, number>; complimentary?: boolean; grace?: boolean; }
export interface Lic {
  id: string; slug: string | null; email: string; company_name: string | null;
  plan: string; plan_version: number; status: string; is_active: boolean;
  complimentary: boolean; complimentary_note: string | null;
  stripe_subscription_status: string | null; current_period_end: string | null; expires_at: string | null;
  grace_until: string | null; in_grace: boolean; limit_mode: 'overage' | 'block';
  config: { plan: string; phone_human: number; phone_ai: number; meetings: number; chatbot: number };
  bonus: Record<string, number>; entitlements: Ent;
  usage: null | { tickets: number; ai_interactions: number; seats_used: number; phone_human_minutes: number; phone_ai_minutes: number; meeting_participant_minutes: number; reported_at: string | null };
  desk_url: string | null; api_url: string | null; provision_status: string; desk_release_tag: string | null; admin_notes: string | null;
  monthly_price_cents: number | null; created_at: string;
}
interface Catalog { plans: Record<string, { key: string; name: string; monthly_usd: number }>; addons: Record<string, { key: string; name: string; packs: Record<string, number> }>; bonus_keys: string[]; }

const STATUS_CLS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  trialing: 'bg-sky-50 text-sky-700 border-sky-200',
  past_due: 'bg-amber-50 text-amber-700 border-amber-200',
  complimentary: 'bg-violet-50 text-violet-700 border-violet-200',
  grace: 'bg-blue-50 text-blue-700 border-blue-200',
  expired: 'bg-red-50 text-red-700 border-red-200',
  suspended: 'bg-slate-100 text-slate-600 border-slate-200',
  pending: 'bg-slate-50 text-slate-500 border-slate-200',
  canceled: 'bg-red-50 text-red-700 border-red-200',
};
const fmtDT = (v?: string | null) => (v ? formatDateLong(new Date(v)) : '–');
const inf = (v: number | null | undefined) => (v === null || v === undefined ? '∞' : String(v));

export default function SuperAdminSection() {
  const { t } = useTranslation('admin');
  const [rows, setRows] = useState<Lic[]>([]);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Lic | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([api.get('/superadmin/licenses'), api.get('/superadmin/catalog')]);
      setRows(l.data?.data || []); setCatalog(c.data?.data || null);
    } catch (e: any) { toast.error(e?.response?.data?.message || t('loadFailed')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter(r => !s || [r.slug, r.email, r.company_name, r.plan, r.status].some(v => (v || '').toLowerCase().includes(s)));
  }, [rows, q]);
  const counts = useMemo(() => rows.reduce<Record<string, number>>((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {}), [rows]);
  const statusLabel = (s: string) => t(`status.${s}`, s);

  return (
    <div className="space-y-3">
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 text-violet-700"><ShieldCheck className="h-4 w-4" /></div>
            <div>
              <div className="text-sm font-bold text-slate-900">{t('title')}</div>
              <div className="text-[11px] text-slate-500">
                {t('licenses', { count: rows.length })} · {Object.entries(counts).map(([k, v]) => `${statusLabel(k)} ${v}`).join(' · ')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('search')}
                className="h-8 w-56 rounded-md border border-slate-200 bg-white pl-8 pr-2 text-xs outline-none focus:border-[#0F99FD]" />
            </div>
            <button onClick={load} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> {t('refresh')}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">{t('col.customer')}</th><th className="px-3 py-2.5">{t('col.status')}</th><th className="px-3 py-2.5">{t('col.plan')}</th>
                  <th className="px-3 py-2.5">{t('col.addons')}</th><th className="px-3 py-2.5">{t('col.usage')}</th><th className="px-3 py-2.5">{t('col.deadline')}</th><th className="px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(r => {
                  const inc = r.entitlements?.included || {};
                  const u = r.usage;
                  const hasAddons = r.config?.phone_human || r.config?.phone_ai || r.config?.meetings || r.config?.chatbot || Object.keys(r.bonus || {}).length;
                  return (
                    <tr key={r.id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-2.5">
                        <div className="font-semibold text-slate-900">{r.slug || '—'}{r.company_name ? <span className="ml-1 font-normal text-slate-500">· {r.company_name}</span> : null}</div>
                        <div className="text-[11px] text-slate-500">{r.email}{r.desk_url ? <a href={r.desk_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-[#0F99FD] hover:underline">{r.desk_url.replace('https://', '')}<ExternalLink className="h-3 w-3" /></a> : null}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge className={`rounded-full border px-2 py-0 text-[10px] font-bold ${STATUS_CLS[r.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{statusLabel(r.status)}</Badge>
                        {!r.is_active && <div className="mt-0.5 text-[10px] text-red-600">{t('inactive')}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold capitalize text-slate-900">{r.entitlements?.plan_name || r.plan}</div>
                        <div className="text-[11px] text-slate-500">{inf(inc.tickets)} {t('units.requests')} · {inf(inc.ai_interactions)} {t('units.ai')} · {inf(inc.seats)} {t('units.seats')}</div>
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-600">
                        {r.config?.phone_human ? <div>{t('units.phone')} {r.config.phone_human} {t('units.min')}</div> : null}
                        {r.config?.phone_ai ? <div>AI {t('units.phone')} {r.config.phone_ai} {t('units.min')}</div> : null}
                        {r.config?.meetings ? <div>{t('units.meetings')} {r.config.meetings}</div> : null}
                        {r.config?.chatbot ? <div>Chatbot {r.config.chatbot}</div> : null}
                        {Object.keys(r.bonus || {}).length ? <div className="text-violet-700"><Gift className="mr-0.5 inline h-3 w-3" />{t('bonus')}</div> : null}
                        {!hasAddons ? '—' : null}
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-600">
                        {u ? <>
                          <div>{u.tickets} {t('units.requests')} · {u.ai_interactions} {t('units.ai')} · {u.seats_used} {t('units.seats')}</div>
                          <div>{u.phone_human_minutes}/{u.phone_ai_minutes} {t('units.phone')} · {u.meeting_participant_minutes} {t('units.meetings')}</div>
                        </> : <span className="text-slate-400">{t('noReport')}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-600">
                        {r.in_grace ? <span className="text-blue-700">{t('graceUntil', { date: fmtDT(r.grace_until) })}</span>
                          : r.current_period_end ? t('periodUntil', { date: fmtDT(r.current_period_end) })
                          : r.expires_at ? t('until', { date: fmtDT(r.expires_at) }) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => setSel(r)} className="rounded-md bg-[#0F99FD] px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-[#0d8ae0]">{t('manage')}</button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && !filtered.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">{t('none')}</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {sel && catalog && (
        <EditDrawer lic={sel} catalog={catalog} onClose={() => setSel(null)} onSaved={(u) => { setRows(rs => rs.map(r => r.id === u.id ? u : r)); setSel(u); }} />
      )}
    </div>
  );
}

function EditDrawer({ lic, catalog, onClose, onSaved }: { lic: Lic; catalog: Catalog; onClose: () => void; onSaved: (l: Lic) => void }) {
  const { t } = useTranslation('admin');
  const [plan, setPlan] = useState(catalog.plans[lic.config?.plan] ? lic.config.plan : 'starter');
  const [packs, setPacks] = useState({ phone_human: lic.config?.phone_human || 0, phone_ai: lic.config?.phone_ai || 0, meetings: lic.config?.meetings || 0, chatbot: lic.config?.chatbot || 0 });
  const [bonus, setBonus] = useState<Record<string, number>>({ ...(lic.bonus || {}) });
  const [complimentary, setComplimentary] = useState(lic.complimentary);
  const [note, setNote] = useState(lic.complimentary_note || '');
  const [limitMode, setLimitMode] = useState(lic.limit_mode);
  const [isActive, setIsActive] = useState(lic.is_active);
  const [graceHours, setGraceHours] = useState(0);
  const [adminNotes, setAdminNotes] = useState(lic.admin_notes || '');
  const [busy, setBusy] = useState(false);
  const planChanged = plan !== lic.config?.plan || (['phone_human', 'phone_ai', 'meetings', 'chatbot'] as const).some(k => packs[k] !== (lic.config?.[k] || 0));

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = { bonus, complimentary, complimentary_note: note, limit_mode: limitMode, is_active: isActive, admin_notes: adminNotes };
      if (planChanged) Object.assign(body, { plan, ...packs });
      if (graceHours > 0) body.grace_hours = graceHours;
      const r = await api.put(`/superadmin/licenses/${lic.id}`, body);
      onSaved(r.data.data);
      toast.success(t('drawer.saved'));
    } catch (e: any) { toast.error(e?.response?.data?.message || t('drawer.saveFailed')); }
    finally { setBusy(false); }
  };
  const packOptions = (key: string) => Object.keys(catalog.addons[key]?.packs || {}).map(Number).sort((a, b) => a - b);

  return (
    <div className="fixed inset-0 z-[200] flex justify-end bg-black/30" onClick={onClose}>
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">{t('drawer.title')}</div>
            <div className="text-lg font-bold text-slate-900">{lic.slug || lic.email}</div>
            <div className="text-xs text-slate-500">{lic.email} · {lic.company_name || '—'} · Stripe: {lic.stripe_subscription_status || '—'}</div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        <section className="mb-4 rounded-lg border border-slate-200 p-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">{t('drawer.package')}</div>
          <div className="grid grid-cols-3 gap-2">
            {Object.values(catalog.plans).map(p => (
              <button key={p.key} onClick={() => setPlan(p.key)} className={`rounded-md border px-2 py-2 text-left text-xs ${plan === p.key ? 'border-[#0F99FD] bg-[#0F99FD]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="font-bold text-slate-900">{p.name}</div><div className="text-[11px] text-slate-500">${p.monthly_usd}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {(['phone_human', 'phone_ai', 'meetings', 'chatbot'] as const).map(key => (
              <label key={key} className="text-xs">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{catalog.addons[key]?.name || key}</div>
                <select value={packs[key]} onChange={e => setPacks(p => ({ ...p, [key]: Number(e.target.value) }))}
                  className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs outline-none focus:border-[#0F99FD]">
                  <option value={0}>{t('drawer.without')}</option>
                  {packOptions(key).map(n => <option key={n} value={n}>{n.toLocaleString()} (${catalog.addons[key].packs[String(n)]})</option>)}
                </select>
              </label>
            ))}
          </div>
          {planChanged && <p className="mt-2 text-[11px] text-amber-700">{t('drawer.packageChanged')}</p>}
        </section>

        <section className="mb-4 rounded-lg border border-slate-200 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500"><Gift className="h-3.5 w-3.5 text-violet-600" /> {t('drawer.bonus')}</div>
          <div className="grid grid-cols-2 gap-2">
            {catalog.bonus_keys.map(k => (
              <label key={k} className="text-xs">
                <div className="mb-1 text-[10px] text-slate-500">{t(`bonusKeys.${k}`, k)}</div>
                <input type="number" min={0} value={bonus[k] || 0} onChange={e => setBonus(b => ({ ...b, [k]: Math.max(0, Number(e.target.value) || 0) }))}
                  className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-[#0F99FD]" />
              </label>
            ))}
          </div>
        </section>

        <section className="mb-4 space-y-3 rounded-lg border border-slate-200 p-3">
          <label className="flex items-center justify-between text-xs">
            <span><span className="font-semibold text-slate-900">{t('drawer.complimentary')}</span><div className="text-[11px] text-slate-500">{t('drawer.complimentaryHint')}</div></span>
            <Switch checked={complimentary} onCheckedChange={setComplimentary} />
          </label>
          {complimentary && <input value={note} onChange={e => setNote(e.target.value)} placeholder={t('drawer.note')} className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs outline-none focus:border-[#0F99FD]" />}
          <label className="flex items-center justify-between text-xs">
            <span><span className="font-semibold text-slate-900">{t('drawer.block')}</span><div className="text-[11px] text-slate-500">{t('drawer.blockHint')}</div></span>
            <Switch checked={limitMode === 'block'} onCheckedChange={v => setLimitMode(v ? 'block' : 'overage')} />
          </label>
          <label className="flex items-center justify-between text-xs">
            <span><span className="font-semibold text-slate-900">{t('drawer.active')}</span><div className="text-[11px] text-slate-500">{t('drawer.activeHint')}</div></span>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </label>
          <div className="flex items-center justify-between gap-3 text-xs">
            <span><span className="font-semibold text-slate-900"><Clock className="mr-1 inline h-3.5 w-3.5" />{t('drawer.extend')}</span><div className="text-[11px] text-slate-500">{lic.grace_until ? t('drawer.currently', { date: fmtDT(lic.grace_until) }) : t('drawer.noGrace')}</div></span>
            <select value={graceHours} onChange={e => setGraceHours(Number(e.target.value))} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
              <option value={0}>—</option><option value={24}>{t('drawer.h24')}</option><option value={72}>{t('drawer.d3')}</option><option value={168}>{t('drawer.d7')}</option><option value={720}>{t('drawer.d30')}</option>
            </select>
          </div>
          <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={2} placeholder={t('drawer.notes')}
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-[#0F99FD]" />
        </section>

        {lic.usage && (
          <section className="mb-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-[11px] text-slate-600">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{t('drawer.usage')}</div>
            {lic.usage.tickets} {t('units.requests')} · {lic.usage.ai_interactions} {t('units.ai')} · {lic.usage.seats_used} {t('units.seats')} · {t('units.phone')} {lic.usage.phone_human_minutes} / AI {lic.usage.phone_ai_minutes} {t('units.min')} · {lic.usage.meeting_participant_minutes} {t('units.meetings')}
            <div className="mt-0.5 text-slate-400">{t('drawer.reported', { date: fmtDT(lic.usage.reported_at) })}{lic.desk_release_tag ? ` · ${t('drawer.release', { tag: lic.desk_release_tag })}` : ''}</div>
          </section>
        )}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">{t('drawer.cancel')}</button>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-[#101319] px-3 py-2 text-xs font-semibold text-white hover:bg-black disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {t('drawer.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
